import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
import ErrorRetry from './components/ErrorRetry';
import Loader from './components/Loader';
import { ENDPOINT_URL } from './constants/endpoints';
import { ConfigProvider } from './context/ConfigContext';
import { setConfigurationData } from './helpers';
import { buildTenantUrl } from './helpers/routing';
import { Http } from './http';
import type { Config } from './models/config';
import { ROUTE_NAMES } from './router/route-names';
import { CONFIG_KEY, LocalStorage } from './storage';
import pkgJson from '../package.json';
import { EnhancedErrorHandler } from './utils/errorHandler';
import { stackMonitor } from './utils/stackMonitor';

const App = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [retryCount, setRetryCount] = useState(0);
  const [configData, setConfigData] = useState<Config | undefined>();
  const navigate = useNavigate();

  //NOTE: Set the version of the portal in the meta tag
  useEffect(() => {
    const versionMeta = document.querySelector('meta[name="adhoc-portal-version"]');
    if (versionMeta) {
      versionMeta.setAttribute('content', pkgJson.version);
    }

    // Start stack monitoring in production to catch near-overflow conditions
    if (process.env.NODE_ENV === 'production') {
      stackMonitor.startMonitoring();
    }

    // Cleanup on unmount
    return () => {
      stackMonitor.stopMonitoring();
    };
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError(undefined);

      const response = await EnhancedErrorHandler.retryWithBackoff(() => new Http().get(ENDPOINT_URL.config), {
        maxAttempts: 3,
        baseDelay: 1000,
        backoff: 'exponential',
        operationId: 'app-config-load',
      });

      const loadedConfig = response?.data?.data;
      setConfigurationData(loadedConfig);
      setConfigData(loadedConfig);

      if (!loadedConfig) {
        throw new Error('Configuration data is empty or invalid');
      }

      const refreshUrl = response.headers['refresh-url'] ?? '';

      if (refreshUrl) {
        navigate(refreshUrl);
      } else {
        // Proceed with standard flow - cookie validation happens on "View session" button click
        navigate(
          buildTenantUrl({
            pathPrefix: '.',
            tenant: response.headers['tenant'],
            routeName: ROUTE_NAMES.chooseLanguage,
          }),
          {
            state: {
              displayMode: loadedConfig.displayMode,
            },
          }
        );
      }

      setLoading(false);
      setRetryCount(0);
    } catch (e) {
      const processed = await EnhancedErrorHandler.processError(e, {
        component: 'App',
        action: 'loadConfiguration',
        additionalData: { retryCount },
      });

      setError(processed.userMessage);
      setLoading(false);
      setRetryCount(prev => prev + 1);
    }
  };

  useEffect(() => {
    loadConfiguration();

    return () => {
      LocalStorage.removeItem(CONFIG_KEY);
    };
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorRetry error={error} retryCount={retryCount} onRetry={loadConfiguration} />;
  }

  return (
    <ErrorBoundary>
      <ConfigProvider initialConfig={configData}>
        <Outlet />
      </ConfigProvider>
    </ErrorBoundary>
  );
};

export default App;
